<template>
    <div>
        <v-scale-transition>
            <a
                v-show="!isLoading"
                href="#"
                class="menu-opener"
                @click.prevent="toggleSidebar"
            >
                <span class="menu-opener__wrapper">
                    <span class="menu-opener__text">
                        <span/>
                    </span>
                </span>
            </a>
        </v-scale-transition>

        <v-card :class="{ 'hasBackButtons': backButton }">
            <v-navigation-drawer
                app
                permanent
                :touchless="true"
                :width="widthSidebar"
                :class="{ 'programInfoPopup': isOpenedProgramInfo }"
                scroll-target="#sidebar-content"
            >
                <v-list
                    ref="sidebar-header"
                    key="sidebar-header"
                    class="pa-0"
                >
                    <v-list-item
                        v-if="isStandalone && !isLoading"
                        class="sidebar-header sidebar-header-standalone"
                    >
                        <v-list-item-content class="pa-6 text-center">
                            <v-img
                                v-if="logos"
                                :src="programLogo || promoterLogo"
                                class="mb-2"
                                width="100%"
                                max-height="100px"
                                contain
                            >
                                <template v-slot:placeholder>
                                    <v-row
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                    >
                                        <v-progress-circular
                                            indeterminate
                                            color="grey lighten-5"
                                        />
                                    </v-row>
                                </template>
                            </v-img>
                            <h2>
                                {{ data.widgetTitle }}
                            </h2>
                            <div
                                v-if="hasProgramInfo"
                                class="pt-4 moreInformation d-flex justify-end"
                            >
                                <a
                                    id="gtm-sidebar-cta-programinfo"
                                    href="#"
                                    @click.prevent="openProgramInfo();"
                                >
                                    <v-icon>
                                        icon-arrow-right
                                    </v-icon>
                                    <span class="body-2">
                                        {{ $t("sidebar.moreInformation") }}
                                    </span>
                                </a>
                            </div>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-else-if="data.heading && data.heading.name && !isLoading"
                        class="sidebar-header pa-6"
                    >
                        <v-list-item-content class="title sidebar-top-header">
                            <v-img
                                :src="programLogo || promoterLogo"
                                class="mb-2"
                                width="100%"
                                max-height="100px"
                                contain
                            >
                                <template v-slot:placeholder>
                                    <v-row
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                    >
                                        <v-progress-circular
                                            indeterminate
                                            color="grey lighten-5"
                                        />
                                    </v-row>
                                </template>
                            </v-img>
                            <h2
                                v-if="(!programLogo && !promoterLogo)"
                                class="sidebar-header__title"
                            >
                                {{ data.heading.name }}
                            </h2>
                            <div
                                v-if="data.heading.fullAddress"
                                class="fullAddress text-center legend pt-3"
                            >
                                {{ data.heading.fullAddress }}
                            </div>
                            <div
                                v-if="hasProgramInfo"
                                class="pt-4 moreInformation d-flex justify-end"
                            >
                                <a
                                    href="#"
                                    @click.prevent="openProgramInfo();"
                                >
                                    <v-icon>
                                        icon-arrow-right
                                    </v-icon>
                                    <span class="body-2">
                                        {{ $t("sidebar.moreInformation") }}
                                    </span>
                                </a>
                            </div>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-show="!isLoading"
                        class="btn-holder text-center"
                    >
                        <v-list-item-content class="pa-0">
                            <div class="d-flex justify-space-around">
                                <v-btn
                                    v-if="!isOpenedProgramInfo && mainMenuButton"
                                    @click.prevent="closeProgramInfo(); mainMenuButton();"
                                    :class="{'separator': backButton}"
                                    class="habiteo-body-2 text-none"
                                    elevation="0"
                                    outlined
                                    :ripple="false"
                                    plain
                                    x-large
                                >
                                    <v-icon left>
                                        icon-home
                                    </v-icon>
                                    {{ $t("sidebar.btnMainMenu") }}
                                </v-btn>

                                <v-btn
                                    v-if="!isOpenedProgramInfo && !mainMenuButton && backViewButton"
                                    @click.prevent="backViewButton();"
                                    :class="{'separator': backButton}"
                                    class="habiteo-body-2 text-none"
                                    elevation="0"
                                    outlined
                                    :ripple="false"
                                    plain
                                    x-large
                                >
                                    <v-icon left>
                                        icon-visibility
                                    </v-icon>
                                    {{ $t("sidebar.btnBackToView") }}
                                </v-btn>

                                <v-btn
                                    v-if="backButton || isOpenedProgramInfo"
                                    @click.prevent="(isOpenedProgramInfo ? closeProgramInfo() : goBack());"
                                    class="habiteo-body-2 text-none"
                                    elevation="0"
                                    outlined
                                    :ripple="false"
                                    plain
                                    x-large
                                >
                                    <v-icon left>
                                        icon-return
                                    </v-icon>
                                    {{ $t("utils.previous") }}
                                </v-btn>
                            </div>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>

                <v-list
                    class="pa-0"
                    key="sidebar-body"
                >
                    <v-sheet
                        v-if="isLoading"
                        class="sidebar-animation"
                    >
                        <v-skeleton-loader
                            max-width="340"
                            type="image, list-item-avatar@12"
                        />
                    </v-sheet>

                    <v-list-item
                        v-else
                        class="sidebar-section pa-0"
                    >
                        <v-sheet
                            id="sidebar-content"
                            class="overflow-y-auto"
                            :maxHeight="sidebarHeight"
                            :width="widthSidebar"
                        >
                            <v-row
                                v-if="buttonTopVisible"
                                id="scroll-top"
                                dense
                                no-gutters
                                class="justify-center scroll-cta"
                            >
                                <v-btn
                                    icon
                                    elevation="0"
                                    @click.prevent="scrollToTop"
                                >
                                    <v-icon class="icon icon-tri-up"/>
                                </v-btn>
                            </v-row>

                            <div v-if="showHotspotName">
                                <v-row class="mx-6 my-8">
                                    <div class="sidebar-hotspot-details hotspot-descr">
                                        <div class="text-title">
                                            <img
                                                src="@/assets/images/sidebar-color-pin.png"
                                                alt="image description"
                                            >
                                            <span class="text-desc">
                                                <h3>
                                                    {{ activeHotspot.name ? activeHotspot.name : activeHotspot.text }}
                                                </h3>
                                            </span>
                                        </div>
                                        <div
                                            class="html-desc"
                                            v-html="activeHotspot.description"
                                        />
                                    </div>
                                </v-row>
                                <v-divider/>
                            </div>

                            <v-row
                                class="mx-8 my-6"
                                v-scroll:#sidebar-content="onScroll"
                            >
                                <ProgramInformation
                                    v-if="isOpenedProgramInfo"
                                    :handler="handler"
                                />
                                <ProgramSidebar
                                    v-else-if="data.type === 'program' && !isOpenedProgramInfo"
                                    :widgetList="data.levels || data.widgetList"
                                    :handler="handler"
                                />
                                <GeneralSidebar
                                    v-else-if="data.type === 'general' && !isOpenedProgramInfo"
                                    :widgetList="data.levels"
                                    :handler="handler"
                                />
                                <CutawaySidebar
                                    v-if="(data.type === 'cutaway' || data.type === 'plot-grid') && !isOpenedProgramInfo"
                                    :data="data"
                                    :handler="handler"
                                    :isOpenedSidebar="isOpenedSidebar"
                                />
                                <PlotSidebar
                                    v-else-if="data.type === 'plot' && !isOpenedProgramInfo"
                                    :data="data"
                                    :handler="handler"
                                    :isOpenedSidebar="isOpenedSidebar"
                                    :isStandalone="isStandalone"
                                />
                                <MapSidebar
                                    v-else-if="data.type === 'map' && !isOpenedProgramInfo"
                                    :data="data"
                                    :handler="handler"
                                    :isOpenedSidebar="isOpenedSidebar"
                                />
                                <ModelsSidebar
                                    v-else-if="data.type === 'models' && !isOpenedProgramInfo"
                                    :data="data"
                                    :handler="handler"
                                />
                                <ConfigurableTourSidebar
                                    v-else-if="data.type === 'configurable-tour' && !isOpenedProgramInfo"
                                    :data="data"
                                    :handler="handler"
                                    :isOpenedSidebar="isOpenedSidebar"
                                />
                                <DocumentationSidebar
                                    v-else-if="data.type === 'documentation' && !isOpenedProgramInfo"
                                    :data="data"
                                    :handler="handler"
                                    :isOpenedSidebar="isOpenedSidebar"
                                />
                            </v-row>

                            <v-row
                                v-if="buttonBottomVisible"
                                id="scroll-bottom"
                                dense
                                no-gutters
                                class="justify-center scroll-cta"
                            >
                                <v-btn
                                    icon
                                    elevation="0"
                                    @click.prevent="scrollToBottom"
                                >
                                    <v-icon class="icon icon-tri-down"/>
                                </v-btn>
                            </v-row>
                        </v-sheet>
                    </v-list-item>
                </v-list>
            </v-navigation-drawer>
        </v-card>
    </div>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import { TweenLite } from 'gsap';
    import { debounce } from 'lodash';
    import { mapActions, mapGetters } from 'vuex';

    import ProgramSidebar from '@/components/sidebar/program/ProgramSidebar';
    import GeneralSidebar from '@/components/sidebar/general/GeneralSidebar';
    import CutawaySidebar from '@/components/sidebar/cutaway/CutawaySidebar';
    import PlotSidebar from '@/components/sidebar/PlotSidebar';
    import MapSidebar from '@/components/sidebar/MapSidebar';
    import ModelsSidebar from '@/components/sidebar/ModelsSidebar';
    import ConfigurableTourSidebar from '@/components/sidebar/configurableTour/ConfigurableTourSidebar';
    import DocumentationSidebar from '@/components/sidebar/DocumentationSidebar';
    import ProgramInformation from '@/components/programInformation/ProgramInformation';

    export default {
        name: 'Sidebar',
        props: [
            'data',
            'backButton',
            'mainMenuButton',
            'backViewButton',
            'goBack',
            'isStandalone',
            'noSubTitle',
            'isOpenedSidebar',
            'widthSidebar',
            'isOpenedProgramInfo',
            'currentWidgetTitle',
            'language'
        ],
        components: {
            ProgramSidebar,
            GeneralSidebar,
            CutawaySidebar,
            PlotSidebar,
            MapSidebar,
            ModelsSidebar,
            ConfigurableTourSidebar,
            DocumentationSidebar,
            ProgramInformation
        },
        data() {
            return {
                hover: false,
                isFavorite: false,
                plotFavList: [],
                sidebarHeight: null,
                scrollValue: 0,
                scrollMax: 0
            };
        },
        computed: {
            ...mapGetters([
                'isLocalstorageAvailable',
                'isLoading',
                'logos',
                'programInfo',
                'isReviewUse'
            ]),
            promoterLogo() {
                return this.logos && 'promoter' in this.logos ? this.logos['promoter'] : null;
            },
            programLogo() {
                return this.logos && 'program' in this.logos ? this.logos['program'] : null;
            },
            programUuid() {
                return (this.data && this.data.program) ? this.data.program.id : null;
            },
            favoriteKey() {
                return `favorite-${this.programUuid}`;
            },
            plotId() {
                return this.data ? this.data.plotId : null;
            },
            favoriteList: {
                get() {
                    return this.plotFavList;
                },
                set(newValue) {
                    localStorage.setItem(this.favoriteKey, JSON.stringify(newValue));
                    this.plotFavList = JSON.parse(localStorage.getItem(this.favoriteKey));
                }
            },
            buttonTopVisible() {
                return this.scrollValue > 0;
            },
            buttonBottomVisible() {
                return this.scrollValue !== this.scrollMax;
            },
            hasProgramInfo() {
                return this.programInfo ? true : false;
            },
            activeHotspot() {
                return this.$props.data.activeHotspot || null;
            },
            showHotspotName() {
                return this.activeHotspot && this.activeHotspot.type === 'digital-model' && this.$props.data.levels && !this.isOpenedProgramInfo && (!this.$props.data.hasOwnProperty('showHotspotName') || this.$props.data.showHotspotName);
            }
        },
        created() {
            this.setIsLoading(true);
        },
        mounted() {
            this.handler();

            document.addEventListener('visibilitychange', this.handler);
            window.addEventListener('DOMContentLoaded', this.handler);
            window.addEventListener('resize', debounce(this.handler, 400));
        },
        methods: {
            ...mapActions([
                'setIsLoading'
            ]),
            handler() {
                if (this.isLocalstorageAvailable)
                    this.plotFavList = localStorage.getItem(this.favoriteKey) ? JSON.parse(localStorage.getItem(this.favoriteKey)) : [];

                this.isFavorite = false;
                if (this.favoriteList && this.plotId)
                    if (this.favoriteList.includes(this.plotId))
                        this.isFavorite = true;

                this.sidebarHeight = this.$refs['sidebar-header'] ? window.innerHeight - this.$refs['sidebar-header'].$el.clientHeight : window.innerHeight;

                let sidebarDOMElement = document.getElementById('sidebar-content');
                if (sidebarDOMElement) {
                    this.scrollValue = sidebarDOMElement.scrollTop;
                    this.scrollMax = sidebarDOMElement.scrollHeight - sidebarDOMElement.clientHeight;
                }
            },
            toggleFavorite() {
                if (!this.isFavorite) {
                    var plotList = this.favoriteList ? this.favoriteList : [];
                    plotList.push(this.plotId);
                    this.favoriteList = plotList;
                    this.isFavorite = true;
                }
                else if (this.plotId) {
                    var plotList = this.favoriteList.filter(plotId => plotId !== this.plotId);
                    this.favoriteList = plotList;
                    this.isFavorite = false;
                }
            },
            openProgramInfo() {
                EventBus.$emit('openProgramInfo');
            },
            closeProgramInfo() {
                EventBus.$emit('closeProgramInfo');
            },
            scrollToTop() {
                this.sidebarHeight = window.innerHeight - this.$refs['sidebar-header'].$el.clientHeight;
                TweenLite.to( document.getElementById('sidebar-content'), .5, { 'scrollTop': 0 });
            },
            scrollToBottom() {
                this.sidebarHeight = window.innerHeight - this.$refs['sidebar-header'].$el.clientHeight;
                let sidebarDOMElement = document.getElementById('sidebar-content');
                let maxScrollTop = sidebarDOMElement.scrollHeight - sidebarDOMElement.clientHeight;
                TweenLite.to(document.getElementById('sidebar-content'), .5, { 'scrollTop': maxScrollTop });
            },
            onScroll(e) {
                this.sidebarHeight = window.innerHeight - this.$refs['sidebar-header'].$el.clientHeight;
                this.scrollValue = e.target.scrollTop;
            },
            toggleSidebar() {
                this.$emit('toggle-sidebar');
            }
        },
        watch: {
            data() {
                this.handler();
            },
            backButton() {
                this.handler();
            },
            isLoading() {
                if (!this.isLoading)
                    this.handler();
            }
        }
    };
</script>


// WEBPACK FOOTER //
// src/components/Sidebar.vue